import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle2, Circle, Clock, AlertCircle, FileText,
  MessageSquare, Send, CreditCard, Plus, Paperclip, Upload, FileSignature,
  FileSearch, ThumbsUp, RotateCcw, Package, Sparkles,
} from 'lucide-react';
import { clientApi } from '@/lib/clientApi';
import { RequirementsForm } from '@/components/client/OnboardingWizard';

const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);
const peso = (n) => (n == null ? '—' : `₱${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/* ── Commercial terms + FX disclosure shown before agreement acceptance and
   payment. Driven entirely by the server-computed `billing` block so the
   customer always sees the USD contractual price AND the actual PHP amount
   they will be charged. ── */
const CommercialTerms = ({ b, showTotal = true }) => {
  if (!b) return null;
  return (
    <div className="bg-white rounded-xl border border-[color:var(--hairline)] p-4 text-sm space-y-2" data-testid="commercial-terms">
      <div className="flex items-center justify-between"><span className="text-muted-foreground">Plan</span><span className="font-semibold text-slate-900">{b.plan_name}</span></div>
      <div className="flex items-center justify-between"><span className="text-muted-foreground">Billing frequency</span><span className="font-semibold text-slate-900 capitalize">{b.billing_cycle}</span></div>
      {b.quote_only ? (
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Price</span><span className="font-semibold text-slate-900">By quote</span></div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Contract price (USD)</span>
            <span className="font-semibold text-slate-900" data-testid="ct-usd">{money(b.amount_usd)}<span className="text-xs text-muted-foreground">/{b.billing_cycle === 'annual' ? 'yr' : 'mo'}</span></span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">You will be charged</span>
            <span className="font-semibold text-slate-900" data-testid="ct-php">{peso(b.amount_php)} <span className="text-xs text-muted-foreground">{b.charge_currency}</span></span>
          </div>
          <p className="text-[11px] text-muted-foreground">Charged in {b.charge_currency} at ₱{b.fx_rate}/USD (FX {b.fx_version}). Your contractual price is in USD.</p>
          {showTotal && (
            <div className="flex items-center justify-between pt-2 border-t border-[color:var(--hairline)]">
              <span className="text-muted-foreground">Total {b.commitment_months}-month commitment</span>
              <span className="font-bold text-slate-900" data-testid="ct-total">{money(b.total_commitment_usd)}</span>
            </div>
          )}
        </>
      )}
      <div className="pt-2 border-t border-[color:var(--hairline)] space-y-1">
        <p className="text-[11px] text-muted-foreground"><strong className="text-slate-600">Renewal:</strong> {b.renewal_terms}</p>
        <p className="text-[11px] text-muted-foreground"><strong className="text-slate-600">Cancellation:</strong> {b.cancellation_terms}</p>
      </div>
    </div>
  );
};

/* ── Progress derived ONLY from real milestone data (never fabricated) ── */
const milestoneProgress = (milestones) => {
  if (!milestones || milestones.length === 0) return null;
  const done = milestones.filter((m) => m.status === 'done').length;
  return Math.round((done / milestones.length) * 100);
};

const MS_ICON = {
  done: { Icon: CheckCircle2, cls: 'text-green-500' },
  in_progress: { Icon: Clock, cls: 'text-brand-blue' },
  blocked: { Icon: AlertCircle, cls: 'text-rose-500' },
  pending: { Icon: Circle, cls: 'text-slate-300' },
};

const Panel = ({ title, icon: Icon, children, action, testid }) => (
  <section className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-5 sm:p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-brand-blue" />} {title}
      </h3>
      {action}
    </div>
    {children}
  </section>
);

const STAGE_TONE = {
  draft: 'bg-slate-100 text-slate-600',
  requirements_submitted: 'bg-brand-blue/10 text-brand-blue',
  scoping: 'bg-amber-100 text-amber-700',
  scope_approved: 'bg-brand-blue/10 text-brand-blue',
  agreement_pending: 'bg-amber-100 text-amber-700',
  agreement_accepted: 'bg-brand-orange/10 text-brand-orange',
  subscribed: 'bg-green-100 text-green-700',
  onboarding: 'bg-brand-blue/10 text-brand-blue',
  in_development: 'bg-brand-blue/10 text-brand-blue',
  launched: 'bg-green-100 text-green-700',
  ongoing: 'bg-green-100 text-green-700',
  paused: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-rose-100 text-rose-700',
  legacy: 'bg-slate-100 text-slate-600',
};

export const ProjectWorkspace = ({ projectId, plans, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [showRevision, setShowRevision] = useState(false);
  const [addonName, setAddonName] = useState('');
  const [addonDesc, setAddonDesc] = useState('');
  const [editingReq, setEditingReq] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reqSaving, setReqSaving] = useState(false);
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const d = await clientApi.projectDetail(projectId); setData(d); }
    catch (e) { toast.error('Could not load this project.'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); clientApi.onboardingQuestions().then((d) => setQuestions(d.questions || [])).catch(() => {}); }, [load]);

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-brand-blue animate-spin" /></div>;
  if (!data) return null;

  const { project, subscription, scope, agreement, milestones, deliverables, addons, files, messages, invoices, next_action: na, billing } = data;
  const plan = (plans || []).find((p) => p.id === project.plan_id);
  const progress = milestoneProgress(milestones);

  const refresh = async () => { await load(); onChanged?.(); };

  const doAction = async (fn, successMsg) => {
    setActing(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Something went wrong. Please try again.'); }
    finally { setActing(false); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try { await clientApi.postMessage(projectId, msgText.trim()); setMsgText(''); await load(); }
    catch (e) { toast.error('Could not send message.'); }
  };

  const reviewDeliverable = async (did, status, feedback) => {
    try { await clientApi.reviewDeliverable(projectId, did, status, feedback || ''); toast.success(status === 'approved' ? 'Deliverable approved.' : 'Revision requested.'); await load(); }
    catch (e) { toast.error('Could not update the deliverable.'); }
  };

  const requestAddon = async () => {
    if (!addonName.trim()) { toast.error('Describe the add-on you need.'); return; }
    try { await clientApi.requestAddon(projectId, { name: addonName.trim(), description: addonDesc.trim() }); setAddonName(''); setAddonDesc(''); toast.success('Add-on request sent.'); await load(); }
    catch (e) { toast.error('Could not send the request.'); }
  };

  /* ── Phase 4 billing actions ── */
  const startSubscriptionCheckout = async () => {
    setActing(true);
    try {
      const res = await clientApi.subscriptionCheckout(projectId);
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.success('Your invoice is ready. Our team will follow up with payment.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start secure checkout. Please try again shortly.');
    } finally { setActing(false); }
  };

  const payInvoiceNow = async (invoiceId) => {
    try {
      const res = await clientApi.payInvoice(invoiceId);
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error('Could not open the payment page.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open the payment page.');
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await clientApi.uploadFile(fd);
      await clientApi.registerFile(projectId, { name: up.filename || file.name, url: up.url, size: up.size || file.size });
      toast.success('File uploaded.'); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  /* ── Next-action banner (drives the onboarding workflow inline) ── */
  const renderAction = () => {
    if (!na) return null;
    const base = 'rounded-2xl border p-5 mb-6';
    if (na.who === 'team' || !na.actionable) {
      return (
        <div className={`${base} border-[color:var(--hairline)] bg-brand-blue/[0.04]`} data-testid="next-action-banner">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-brand-blue shrink-0" />
            <p className="text-sm font-medium text-slate-700">{na.label}</p>
          </div>
        </div>
      );
    }
    if (na.action === 'requirements') {
      return (
        <div className={`${base} border-brand-blue/30 bg-brand-blue/[0.05]`} data-testid="next-action-banner">
          <p className="text-sm font-semibold text-slate-900 mb-3">{na.label}</p>
          <button onClick={() => setEditingReq(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors" data-testid="action-complete-requirements"><FileText className="w-4 h-4" /> Complete your requirements</button>
        </div>
      );
    }
    if (na.action === 'approve_scope') {
      return (
        <div className={`${base} border-amber-300 bg-amber-50`} data-testid="next-action-banner">
          <div className="flex items-center gap-2 mb-2"><FileSearch className="w-5 h-5 text-amber-700" /><p className="text-sm font-bold text-amber-800">{na.label}</p></div>
          {scope && (
            <div className="bg-white rounded-xl border border-amber-200 p-4 mb-3">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{scope.summary}</p>
              {(scope.deliverables || []).length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {scope.deliverables.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-brand-blue mt-0.5 shrink-0" /> {d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={() => doAction(() => clientApi.approveScope(projectId), 'Scope approved.')} disabled={acting} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition-colors disabled:opacity-50" data-testid="action-approve-scope"><ThumbsUp className="w-4 h-4" /> Approve scope</button>
            <button onClick={() => setShowRevision((s) => !s)} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors" data-testid="action-request-scope-revision"><RotateCcw className="w-4 h-4" /> Request changes</button>
          </div>
          {showRevision && (
            <div className="mt-3">
              <textarea value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} rows={3} placeholder="What would you like changed?" className="w-full px-3.5 py-2.5 rounded-xl border border-amber-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none" data-testid="scope-revision-note" />
              <button onClick={() => doAction(() => clientApi.requestScopeRevision(projectId, revisionNote), 'Sent to our team.').then(() => { setShowRevision(false); setRevisionNote(''); })} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors"><Send className="w-3 h-3" /> Send request</button>
            </div>
          )}
        </div>
      );
    }
    if (na.action === 'accept_agreement') {
      return (
        <div className={`${base} border-brand-orange/40 bg-brand-orange/[0.06]`} data-testid="next-action-banner">
          <div className="flex items-center gap-2 mb-2"><FileSignature className="w-5 h-5 text-brand-orange" /><p className="text-sm font-bold text-slate-900">{na.label}</p></div>
          {scope?.summary && (
            <div className="bg-white rounded-xl border border-[color:var(--hairline)] p-4 mb-3">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-1">Approved scope</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{scope.summary}</p>
              {(scope.deliverables || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {scope.deliverables.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700"><CheckCircle2 className="w-3.5 h-3.5 text-brand-blue mt-0.5 shrink-0" /> {d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <CommercialTerms b={billing} />
          {agreement && (
            <p className="text-[11px] text-muted-foreground mt-2 mb-3">Agreement terms version <strong>{agreement.terms_version}</strong> · {agreement.commitment_months}-month commitment. By accepting you agree to these commercial terms; your managed subscription begins after the first payment.</p>
          )}
          <button onClick={() => doAction(() => clientApi.acceptAgreement(projectId), 'Agreement accepted.')} disabled={acting} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark transition-colors disabled:opacity-50" data-testid="action-accept-agreement"><FileSignature className="w-4 h-4" /> Review &amp; accept agreement</button>
        </div>
      );
    }
    if (na.action === 'payment') {
      const providerReady = billing?.provider_ready;
      return (
        <div className={`${base} border-brand-orange/40 bg-brand-orange/[0.06]`} data-testid="next-action-banner">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5 text-brand-orange" /><p className="text-sm font-bold text-slate-900">{na.label}</p></div>
          <CommercialTerms b={billing} />
          <div className="mt-3">
            {billing?.quote_only ? (
              <p className="text-xs text-muted-foreground text-center">This is a custom plan. Our team will send your first invoice with a secure payment link shortly.</p>
            ) : providerReady ? (
              <>
                <button onClick={startSubscriptionCheckout} disabled={acting} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark transition-colors disabled:opacity-50" data-testid="action-pay-first-invoice">{acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay &amp; start development</button>
                <p className="text-xs text-muted-foreground mt-2 text-center">You'll be taken to our secure PayMongo checkout. Development begins once your first payment clears.</p>
              </>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center" data-testid="payment-setup-pending">
                <p className="text-sm font-semibold text-amber-800">Payment Setup Pending</p>
                <p className="text-xs text-amber-700 mt-1">Secure online payment isn't available in this environment yet. Your invoice is ready and our team will send you a secure payment link to complete the first payment.</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const subStatus = subscription?.status || 'inactive';

  return (
    <div data-testid="project-workspace">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-slate-900 transition-colors mb-2" data-testid="workspace-back"><ArrowLeft className="w-4 h-4" /> All projects</button>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900" data-testid="workspace-title">{project.name}</h1>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-brand-blue/10 text-brand-blue uppercase tracking-wide">{plan?.name || project.plan_id || 'No plan'}</span>
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${STAGE_TONE[project.lifecycle_state] || 'bg-slate-100 text-slate-600'}`} data-testid="workspace-stage">{project.state_label}</span>
          </div>
        </div>
      </div>

      {renderAction()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Main column ── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress & milestones */}
          <Panel title="Progress & Milestones" icon={Sparkles} testid="panel-milestones">
            {progress !== null ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-brand-blue rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
                  <span className="text-sm font-bold text-slate-900">{progress}%</span>
                </div>
                <div className="space-y-2.5">
                  {milestones.map((m) => {
                    const t = MS_ICON[m.status] || MS_ICON.pending; const Icon = t.Icon;
                    return (
                      <div key={m.id} className="flex items-center gap-3" data-testid={`milestone-${m.id}`}>
                        <Icon className={`w-4 h-4 ${t.cls} shrink-0`} />
                        <span className="text-sm text-slate-700 flex-1">{m.title}</span>
                        {m.due_date && <span className="text-xs text-muted-foreground">{m.due_date}</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Milestones will appear here once your project kicks off. We never show made-up progress.</p>
            )}
          </Panel>

          {/* Deliverables */}
          <Panel title="Deliverables & Approvals" icon={Package} testid="panel-deliverables">
            {(deliverables || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Your team will share deliverables here for review and sign-off.</p>
            ) : (
              <div className="space-y-3">
                {deliverables.map((d) => (
                  <div key={d.id} className="rounded-xl border border-[color:var(--hairline)] p-4" data-testid={`deliverable-${d.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{d.title}</p>
                        {d.description && <p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>}
                        {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1 mt-1"><Paperclip className="w-3 h-3" /> View file</a>}
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-md shrink-0 ${d.status === 'approved' ? 'bg-green-50 text-green-600' : d.status === 'revision' ? 'bg-brand-orange/10 text-brand-orange' : 'bg-secondary text-slate-500'}`}>{d.status === 'approved' ? 'Approved' : d.status === 'revision' ? 'Revision' : 'Pending'}</span>
                    </div>
                    {d.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => reviewDeliverable(d.id, 'approved')} className="flex-1 py-2 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors" data-testid={`approve-deliverable-${d.id}`}><CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Approve</button>
                        <button onClick={() => { const fb = window.prompt('What changes would you like?') || ''; if (fb) reviewDeliverable(d.id, 'revision', fb); }} className="flex-1 py-2 rounded-lg bg-brand-orange/10 text-brand-orange text-xs font-semibold hover:bg-brand-orange/20 transition-colors" data-testid={`revise-deliverable-${d.id}`}><RotateCcw className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Request revision</button>
                      </div>
                    )}
                    {d.status === 'revision' && d.feedback && <div className="mt-2 text-xs text-muted-foreground bg-brand-orange/5 rounded-lg p-2.5"><strong>Your feedback:</strong> {d.feedback}</div>}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Requirements & files */}
          <Panel title="Requirements & Files" icon={FileText} testid="panel-requirements"
            action={project.requirements_status === 'submitted' && (
              <button onClick={() => setEditingReq((v) => !v)} className="text-xs font-semibold text-brand-blue hover:text-brand-blue-dark" data-testid="edit-requirements-toggle">{editingReq ? 'Close' : 'Update'}</button>
            )}>
            {editingReq ? (
              <RequirementsForm
                questions={questions}
                initial={project.requirements || {}}
                saving={reqSaving}
                submitting={reqSubmitting}
                onSaveDraft={async (ans) => { setReqSaving(true); try { await clientApi.saveRequirements(projectId, ans); toast.success('Saved.'); setEditingReq(false); await refresh(); } catch (e) { toast.error('Could not save.'); } finally { setReqSaving(false); } }}
                onSubmit={async (ans) => { setReqSubmitting(true); try { await clientApi.submitRequirements(projectId, ans); toast.success('Requirements submitted.'); setEditingReq(false); await refresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit.'); } finally { setReqSubmitting(false); } }}
              />
            ) : (
              <>
                {project.requirements && Object.values(project.requirements).some((v) => (v || '').trim()) ? (
                  <dl className="space-y-3 mb-4">
                    {questions.filter((q) => (project.requirements?.[q.id] || '').trim()).map((q) => (
                      <div key={q.id}>
                        <dt className="text-xs font-semibold text-muted-foreground">{q.label}</dt>
                        <dd className="text-sm text-slate-800 mt-0.5">{project.requirements[q.id]}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">No requirements captured yet.</p>
                )}
                <div className="border-t border-[color:var(--hairline)] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Files</p>
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[color:var(--hairline)] text-xs font-semibold text-slate-700 hover:bg-secondary/60 transition-colors disabled:opacity-50" data-testid="upload-file-btn">{uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload</button>
                    <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} data-testid="file-input" />
                  </div>
                  {(files || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No files uploaded yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {files.map((f) => (
                        <li key={f.id} className="flex items-center gap-2 text-sm"><Paperclip className="w-3.5 h-3.5 text-muted-foreground" /><a href={f.url} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline truncate">{f.name}</a><span className="text-[11px] text-muted-foreground ml-auto">{f.uploaded_by}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </Panel>

          {/* Messages */}
          <Panel title="Messages & Support" icon={MessageSquare} testid="panel-messages">
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4" data-testid="messages-thread">
              {(messages || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Questions? Message your team here anytime.</p>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === 'client' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.sender === 'client' ? 'bg-brand-blue text-white' : 'bg-secondary text-slate-800'}`}>
                    <p className="text-[11px] font-semibold opacity-80 mb-0.5">{m.sender_name}</p>
                    <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={1} placeholder="Write a message…" className="flex-1 px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/25 resize-none" data-testid="message-input" />
              <button onClick={sendMessage} disabled={!msgText.trim()} className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-blue text-white hover:bg-brand-blue-dark transition-colors disabled:opacity-40 shrink-0" data-testid="send-message-btn"><Send className="w-4 h-4" /></button>
            </div>
          </Panel>
        </div>

        {/* ── Sidebar column ── */}
        <div className="space-y-6">
          {/* Plan & billing */}
          <Panel title="Plan & Billing" icon={CreditCard} testid="panel-billing">
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Plan</span><span className="font-semibold text-slate-900">{plan?.name || project.plan_id || '—'}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Billing</span><span className="font-semibold text-slate-900 capitalize">{project.billing_cycle}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Price</span><span className="font-semibold text-slate-900">{plan?.quote_only ? 'By quote' : money(project.billing_cycle === 'annual' ? plan?.annual_usd : plan?.monthly_usd)}{!plan?.quote_only && <span className="text-xs text-muted-foreground">/{project.billing_cycle === 'annual' ? 'yr' : 'mo'}</span>}</span></div>
              {billing && !billing.quote_only && (
                <div className="flex items-center justify-between"><span className="text-muted-foreground">Charged</span><span className="font-semibold text-slate-900" data-testid="billing-charge-php">{peso(billing.amount_php)} <span className="text-xs text-muted-foreground">{billing.charge_currency}</span></span></div>
              )}
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Subscription</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${subStatus === 'active' ? 'bg-green-100 text-green-700' : subStatus === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-testid="subscription-status">{subStatus}</span>
              </div>
              {subscription?.next_billing_at && subStatus === 'active' && (
                <div className="flex items-center justify-between"><span className="text-muted-foreground">Next billing</span><span className="font-semibold text-slate-900" data-testid="next-billing-date">{new Date(subscription.next_billing_at).toLocaleDateString()}</span></div>
              )}
            </div>
            {(invoices || []).length > 0 && (
              <div className="mt-4 border-t border-[color:var(--hairline)] pt-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Invoices</p>
                <ul className="space-y-2" data-testid="client-invoices">
                  {invoices.map((inv, i) => {
                    const unpaid = inv.status && !['paid', 'void'].includes(inv.status);
                    return (
                      <li key={inv.invoice_id || i} className="flex items-center justify-between gap-2 text-sm" data-testid={`client-invoice-${inv.invoice_id || i}`}>
                        <div className="min-w-0">
                          <span className="text-slate-800 font-medium">{money(inv.total ?? inv.amount)}</span>
                          <span className="text-[11px] text-muted-foreground ml-2 capitalize">{(inv.kind || '').replace(/_/g, ' ') || inv.status}</span>
                        </div>
                        {unpaid ? (
                          billing?.provider_ready ? (
                            <button onClick={() => payInvoiceNow(inv.invoice_id)} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-brand-orange text-white text-[11px] font-bold hover:bg-brand-orange-dark transition-colors" data-testid={`pay-invoice-${inv.invoice_id}`}><CreditCard className="w-3 h-3" /> Pay</button>
                          ) : (
                            <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-100 text-amber-700" data-testid={`invoice-awaiting-link-${inv.invoice_id}`}>Link coming</span>
                          )
                        ) : (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 capitalize ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{inv.status}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Panel>

          {/* Add-ons */}
          <Panel title="Add-on Requests" icon={Plus} testid="panel-addons">
            {(addons || []).length > 0 && (
              <ul className="space-y-2 mb-4">
                {addons.map((a) => (
                  <li key={a.id} className="rounded-xl border border-[color:var(--hairline)] p-3" data-testid={`addon-${a.id}`}>
                    <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-900">{a.name}</span><span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-secondary text-slate-500 capitalize">{a.status}</span></div>
                    {a.description && <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>}
                  </li>
                ))}
              </ul>
            )}
            <input value={addonName} onChange={(e) => setAddonName(e.target.value)} placeholder="Add-on name (e.g. SMS reminders)" className="w-full px-3 py-2 rounded-xl border border-[color:var(--hairline)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/25 mb-2" data-testid="addon-name" />
            <textarea value={addonDesc} onChange={(e) => setAddonDesc(e.target.value)} rows={2} placeholder="What should it do? (optional)" className="w-full px-3 py-2 rounded-xl border border-[color:var(--hairline)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/25 resize-none mb-2" data-testid="addon-desc" />
            <button onClick={requestAddon} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-brand-blue/30 text-brand-blue text-sm font-semibold hover:bg-brand-blue/5 transition-colors" data-testid="request-addon-btn"><Plus className="w-4 h-4" /> Request add-on</button>
          </Panel>
        </div>
      </div>
    </div>
  );
};
